import dayjs from 'dayjs'
import styled from '@emotion/styled'
import { ReactElement } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { FormLabel, FormControl, Typography } from '@mui/material'
import { READABLE_DATE_FORMAT } from '@archax/shared-types'
import { DateTimePicker as MUIDateTimePicker, DateTimePickerProps as MUIDateTimePickerProps } from '@mui/x-date-pickers'

const StyledFormLabel = styled(FormLabel)<any>(({ theme, hasError }) => ({
  color: hasError ? theme?.error?.main : theme?.palette?.grey[700],
}))

type Props = MUIDateTimePickerProps<Date> & {
  name: string
  customLabel?: boolean
  error?: string
  onChange: (selectedDate: Date) => void
}
export function DateTimePicker(props: Props): ReactElement {
  return (
    <FormControl fullWidth>
      {props.customLabel && (
        <StyledFormLabel
          hasError
          data-testid={`date-time-picker-${props.name}__label`}
          id={`date-time-picker-${props.name}__label`}
        >
          {props.label}
        </StyledFormLabel>
      )}
      <MUIDateTimePicker
        data-testid={`date-time-picker-${props.name}`}
        value={props.value ? dayjs(props.value) : null}
        onChange={(selectedDate: { toDate: () => Date } | null) => {
          props.onChange(selectedDate?.toDate() as Date)
        }}
        label={props.customLabel ? '' : props.label}
        slotProps={
          {
            ...(props.slotProps || {}),
            textField: {
              ...(props?.slotProps?.textField || {}),
              error: !!props.error,
            },
          } as any
        }
      />
      {!!props.error && (
        <Typography variant="caption" color="error">
          {props.error}
        </Typography>
      )}
    </FormControl>
  )
}

export function DateTimePickerWithForm(props: Omit<Props, 'onChange'>): ReactElement {
  const { control, getValues } = useFormContext()

  return (
    <Controller
      name={props.name}
      control={control}
      render={({ field: { onChange }, fieldState }) => {
        return (
          <DateTimePicker
            format={READABLE_DATE_FORMAT}
            {...props}
            value={getValues(props.name)}
            onChange={(selectedItem) => {
              onChange(selectedItem)
            }}
            error={fieldState.error?.message}
          />
        )
      }}
    />
  )
}
